import { Badge, buttonVariants } from '@napaicp/ui'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import napaicpLogo from '@/assets/napaicp-logo.png'
import { usePublicCompetitions } from '@/features/public/api'

/**
 * The landing page's opening section, at `/`. PR-053 (v3.1.2, TASK-U-009):
 * `/` renders for every visitor now, signed in or not — `authenticated`
 * gates the primary "立即登入" CTA, since UX-023/AC-042 require that an
 * authenticated session never shows the login CTA as its primary action
 * (ISSUE-PREVIEW-20260820-004). The secondary "explore roles" anchor stays
 * either way — it is not a login action.
 */
export function HeroSection({ authenticated = false }: { authenticated?: boolean }) {
  const { t } = useTranslation('home')
  const query = usePublicCompetitions()
  const count = query.data?.competitions.length

  return (
    <section className="grid items-center gap-6 pt-4 pb-1 md:grid-cols-2 md:gap-10 md:pt-6 md:pb-2">
      <div className="flex flex-col gap-6">
        {/* Only rendered once a real count is known — no placeholder text or
            guessed number while loading or on error. */}
        {count !== undefined ? (
          <Badge variant="success" className="w-fit gap-1.5 py-1 text-sm">
            <span aria-hidden="true" className="size-2 rounded-full bg-current" />
            {t('hero.liveBadge', { count })}
          </Badge>
        ) : null}
        <h1 className="text-2xl font-semibold tracking-tight sm:text-3xl">{t('hero.title')}</h1>
        <p className="text-lg font-medium text-foreground">{t('hero.subtitle')}</p>
        <p className="text-lg text-muted-foreground">{t('hero.description')}</p>
        <div className="flex flex-wrap gap-3">
          {authenticated ? null : (
            <Link className={buttonVariants({ size: 'lg' })} to="/login">
              {t('hero.primaryCta')}
            </Link>
          )}
          <a className={buttonVariants({ variant: 'outline', size: 'lg' })} href="#roles">
            {t('hero.secondaryCta')}
          </a>
        </div>
      </div>

      <div
        aria-hidden="true"
        className="flex h-56 items-center justify-center rounded-xl border border-border bg-muted p-6 md:h-full md:min-h-72"
      >
        <img src={napaicpLogo} alt="" className="max-h-full max-w-full object-contain" />
      </div>
    </section>
  )
}
